import React, { useState, useEffect, useRef } from 'react'
import { useAppLanguage } from '../../i18n'

import type { CollectionFormData } from './types'
import { KB_CONTENT_LANGUAGE_ISO, labelForKbIso } from './kbLanguageOptions'

type CollectionCreateModalProps = {
  open: boolean
  onClose: () => void
  onCreate: (data: CollectionFormData) => Promise<boolean>
}

/**
 * Modal de création d'une nouvelle collection
 * Responsabilité unique : formulaire de création
 */
export default function CollectionCreateModal({
  open,
  onClose,
  onCreate,
}: CollectionCreateModalProps) {
  const { t } = useAppLanguage()
  const [title, setTitle] = useState('')
  const [contentLanguage, setContentLanguage] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset et focus à l'ouverture
  useEffect(() => {
    if (open) {
      setTitle('')
      setContentLanguage('')
      setError(null)
      setSaving(false)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  // Fermer avec Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError(t.knowledges.collectionCreate.titleRequired)
      return
    }
    setSaving(true)
    setError(null)
    const success = await onCreate({
      title: title.trim(),
      content_language: contentLanguage.trim() ? contentLanguage.trim().toLowerCase() : null,
    })
    setSaving(false)
    if (success) {
      onClose()
    } else {
      setError(t.knowledges.collectionCreate.creationError)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      {/* Modal */}
      <aside className="absolute inset-y-0 right-0 w-full max-w-md border-l border-emerald-100/70 bg-white/95 shadow-2xl dark:border-emerald-900/50 dark:bg-zinc-950/95">
        <div className="flex h-full flex-col">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-emerald-100/70 px-6 py-4 dark:border-emerald-900/50">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {t.knowledges.collectionCreate.title}
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {t.knowledges.collectionCreate.subtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-zinc-200/70 px-3 py-1 text-sm text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              {t.common.close}
            </button>
          </header>

          {/* Contenu */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <section className="space-y-3">
              <label 
                htmlFor="collection-title"
                className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400"
              >
                {t.knowledges.collectionCreate.collectionTitleLabel}
              </label>
              <input
                ref={inputRef}
                id="collection-title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t.knowledges.collectionCreate.placeholder}
                disabled={saving}
                className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/50"
                data-onboarding="collection-title-input"
              />
              <div className="space-y-3 pt-2">
                <label
                  htmlFor="collection-kb-lang"
                  className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400"
                >
                  {t.knowledges.collectionCreate.kbLanguageLabel}
                </label>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  {t.knowledges.collectionCreate.kbLanguageHint}
                </p>
                <select
                  id="collection-kb-lang"
                  value={contentLanguage}
                  onChange={e => setContentLanguage(e.target.value)}
                  disabled={saving}
                  className="w-full rounded-xl border border-emerald-100/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-900/80 dark:text-zinc-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/50"
                >
                  {KB_CONTENT_LANGUAGE_ISO.map(iso => (
                    <option key={iso || 'auto'} value={iso}>
                      {labelForKbIso(iso, t.knowledges.kbContentLanguage)}
                    </option>
                  ))}
                </select>
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
              <button
                type="submit"
                disabled={saving || !title.trim()}
                className="inline-flex w-full justify-center rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:opacity-50"
              >
                {saving ? t.knowledges.collectionCreate.creating : t.knowledges.collectionCreate.create}
              </button>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                {t.knowledges.collectionCreate.tipsTitle}
              </h3>
              <ul className="space-y-3 text-sm text-zinc-600 dark:text-zinc-300">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {t.knowledges.collectionCreate.tip1}
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {t.knowledges.collectionCreate.tip2}
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {t.knowledges.collectionCreate.tip3}
                </li>
              </ul>
            </section>
          </form>
        </div>
      </aside>
    </div>
  )
}
